import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../hooks/useAuth'

// ── medications ────────────────────────────────────────────────
export const useMedications = () => {
  const { household } = useAuth()
  const qc  = useQueryClient()
  const key = ['medications', household?.id]

  const query = useQuery({
    queryKey: key,
    enabled:  !!household?.id,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('medications')
        .select('*, profiles!member_id(id, display_name, member_color)')
        .eq('household_id', household.id)
        .order('name')
      if (error) throw error
      return data ?? []
    },
  })

  const { user } = useAuth()

  const create = useMutation({
    mutationFn: async (payload) => {
      const { data, error } = await supabase
        .from('medications')
        .insert({ ...payload, household_id: household.id, created_by: user.id })
        .select()
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  })

  const update = useMutation({
    mutationFn: async ({ id, ...payload }) => {
      const { data, error } = await supabase
        .from('medications')
        .update(payload)
        .eq('id', id)
        .select()
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  })

  const remove = useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase.from('medications').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  })

  return { ...query, create, update, remove }
}

// ── medication logs ────────────────────────────────────────────
export const useMedicationLogs = (medicationId) => {
  const { household, user } = useAuth()
  const qc  = useQueryClient()
  const key = ['medication-logs', medicationId]

  const query = useQuery({
    queryKey: key,
    enabled:  !!medicationId && !!household?.id,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('medication_logs')
        .select('*, taken_by_profile:profiles!taken_by(id, display_name)')
        .eq('medication_id', medicationId)
        .order('taken_at', { ascending: false })
        .limit(50)
      if (error) throw error
      return data ?? []
    },
  })

  const logDose = useMutation({
    mutationFn: async ({ takenAt, skipped, skipReason, notes, memberId }) => {
      const { data, error } = await supabase
        .from('medication_logs')
        .insert({
          medication_id: medicationId,
          household_id:  household.id,
          member_id:     memberId,
          taken_at:      takenAt,
          taken_by:      user.id,
          skipped:       skipped ?? false,
          skip_reason:   skipReason || null,
          notes:         notes || null,
        })
        .select()
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  })

  return { ...query, logDose }
}

// ── upcoming refills ───────────────────────────────────────────
export const useUpcomingRefills = (daysAhead = 14) => {
  const { household } = useAuth()
  return useQuery({
    queryKey: ['upcoming-refills', household?.id, daysAhead],
    enabled:  !!household?.id,
    queryFn:  async () => {
      const { data, error } = await supabase.rpc('get_upcoming_refills', {
        p_household_id: household.id,
        p_days_ahead:   daysAhead,
      })
      if (error) throw error
      return data ?? []
    },
  })
}
